import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Camera } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.js';
import { eventsService } from '../../services/firestoreService.js';

export const CreateEventModal = ({ isOpen, onClose, onEventCreated }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '',
    time: '',
    location: '',
    photoSettings: {
      allowedTypes: ['jpg', 'png', 'jpeg'],
      moderation: false
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.uid) {
      setError('Debes estar autenticado para crear eventos');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Preparar datos del evento
      const eventData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        date: new Date(`${formData.date} ${formData.time || '00:00'}`),
        location: formData.location.trim(),
        organizerId: user.uid,
        organizerName: user.displayName || user.email,
        photoSettings: formData.photoSettings,
        settings: { autoApprove: !formData.photoSettings.moderation },
        status: 'active' // Por defecto activo
      };

      // Validaciones básicas
      if (!eventData.name) {
        throw new Error('El nombre del evento es obligatorio');
      }
      if (!eventData.date || isNaN(eventData.date.getTime())) {
        throw new Error('La fecha del evento es obligatoria');
      }
      if (eventData.date < new Date()) {
        throw new Error('La fecha del evento no puede ser en el pasado');
      }

      // Generar código de unión único
      const joinCode = await eventsService.generateUniqueJoinCode();
      eventData.joinCode = joinCode;

      // Crear el evento en Firestore
      const newEvent = await eventsService.createEvent(eventData);
      
      console.log('Evento creado exitosamente:', newEvent);
      
      // Limpiar formulario
      setFormData({
        name: '',
        description: '',
        date: '',
        time: '',
        location: '',
        photoSettings: {
          allowedTypes: ['jpg', 'png', 'jpeg'],
          moderation: false
        }
      });

      // Cerrar modal y notificar
      onClose();
      if (onEventCreated) {
        onEventCreated();
      }

      // Notificación coherente con la UI
      try {
        await navigator.clipboard.writeText(joinCode);
        toast.success(`Evento creado. Código copiado: ${joinCode}`);
      } catch (_) {
        toast.success(`Evento creado. Código: ${joinCode}`);
      }

    } catch (error) {
      console.error('Error creating event:', error);
      setError(error.message || 'Error al crear el evento. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        date: '',
        time: '',
        location: '',
        photoSettings: {
          allowedTypes: ['jpg', 'png', 'jpeg'],
          moderation: false
        }
      });
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Crear Nuevo Evento
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Evento *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                placeholder="Ej: Fiesta de 15 - María"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                disabled={loading}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hora
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ubicación
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                placeholder="Ej: Salón Principal"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              disabled={loading}
              maxLength={280}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              placeholder="Describe tu evento..."
            />
            <div className="mt-1 text-xs text-gray-500">{(formData.description || '').length}/280</div>
          </div>

          {/* Campo removido: Máximo de Participantes */}

          {/* Configuración de fotos */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2" />
              Configuración de Fotos
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Campo removido: Tamaño máximo de archivo (MB) */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderación de fotos
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="moderation"
                    checked={formData.photoSettings.moderation}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      photoSettings: {
                        ...prev.photoSettings,
                        moderation: e.target.checked
                      }
                    }))}
                    disabled={loading}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="moderation" className="ml-2 text-sm text-gray-700">
                    Requerir aprobación antes de mostrar
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creando...
                </>
              ) : (
                'Crear Evento'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
